import { useCallback, useEffect, useRef, useState } from 'react';
import { callApi } from '../../../../utils/apiCall';
import { VOICE_URLS } from './voiceConnections.api';
import { LINE_URLS, normaliseUnregistered, setUpRequests, setUpError, sumBackfill } from './voiceLines.api';

const EMPTY = { lines: [], registered: [], ownedElsewhere: 0, sharedAccount: false };

// Lines that answer "not set up" for this provider/backend are skipped, not errors:
// 400 = not an Elevate connection, 404 = older backend, 403 = not an admin.
const SKIP = new Set([400, 403, 404]);

// Phone numbers taking calls that aren't set up, across the practice's
// connections. The Integrations card passes its active Elevate connection ids;
// Call Logs passes null and the hook reads the connection list itself. Admin
// only on the backend, so callers pass enabled=false for anyone else. A reply
// that lands after a practice switch is dropped.
export default function useVoiceLines(practiceId, { connectionIds = null, enabled = true } = {}) {
    const [state, setState] = useState({ practiceId: null, ...EMPTY, loading: false, loaded: false, error: null });
    const [saving, setSaving] = useState(false);
    const current = useRef(practiceId);
    current.current = practiceId;
    const idsKey = connectionIds ? connectionIds.map(String).join(',') : null;

    const load = useCallback(async () => {
        if (!practiceId || !enabled) return;
        const forPractice = practiceId;
        const stale = () => String(current.current) !== String(forPractice);
        setState((s) => (String(s.practiceId) === String(forPractice)
            ? { ...s, loading: true, error: null }
            : { practiceId: forPractice, ...EMPTY, loading: true, loaded: false, error: null }));

        let ids = idsKey === null ? null : idsKey.split(',').filter(Boolean);
        if (ids === null) {
            const list = await callApi(VOICE_URLS.list(forPractice), 'GET');
            if (stale()) return;
            const rows = Array.isArray(list.data) ? list.data : (list.data?.results || []);
            ids = list.ok ? rows.filter((c) => c.is_enabled !== false).map((c) => String(c.id)) : [];
        }
        const replies = await Promise.all(ids.map((id) => callApi(LINE_URLS.unregistered(forPractice, id), 'GET', null, false, { bustCache: true }).then((r) => [id, r])));
        if (stale()) return;
        const failed = replies.find(([, r]) => !r.ok && !SKIP.has(r.status));
        const parsed = replies.filter(([, r]) => r.ok).map(([id, r]) => normaliseUnregistered(r.data, id));
        setState({
            practiceId: forPractice,
            lines: parsed.flatMap((p) => p.lines),
            registered: parsed.flatMap((p) => p.registered),
            ownedElsewhere: parsed.reduce((n, p) => n + p.ownedElsewhere, 0),
            sharedAccount: parsed.some((p) => p.sharedAccount),
            loading: false,
            loaded: true,
            error: failed ? (failed[1].data?.detail || 'Could not check phone lines') : null,
        });
    }, [practiceId, enabled, idsKey]);

    useEffect(() => { load(); }, [load]);

    // Saves rows ({ connectionId, number, name, site }) per connection in
    // batches of 50, stopping at the first refusal. Resolves
    // { ok, saved, backfill, message }. A status-0 reply may have saved, so the
    // list is reloaded and the user is told to check rather than retry.
    const setUpLines = useCallback(async (rows) => {
        const forPractice = practiceId;
        setSaving(true);
        const done = [];
        let failure = null;
        // eslint-disable-next-line no-restricted-syntax
        for (const req of setUpRequests(rows)) {
            // Sequential on purpose: each batch re-labels calls and queues recordings.
            // eslint-disable-next-line no-await-in-loop
            const response = await callApi(LINE_URLS.setUp(forPractice, req.connectionId), 'POST', req.body, false, { timeout: 30000 });
            if (!response.ok) { failure = response; break; }
            done.push(response.data);
        }
        setSaving(false);
        if (String(current.current) !== String(forPractice)) return { ok: false, saved: 0, message: null };
        const saved = done.reduce((n, d) => n + (Array.isArray(d?.lines) ? d.lines.length : 0), 0);
        await load();
        if (!failure) return { ok: true, saved, backfill: sumBackfill(done), message: null };
        const prefix = saved ? `${saved} saved, then: ` : '';
        if (failure.status === 0) return { ok: false, saved, message: `${prefix}No reply from the server. The list has been refreshed: check which lines were saved before trying again.` };
        return { ok: false, saved, message: `${prefix}${setUpError(failure.data) || 'Could not set up these lines.'}` };
    }, [practiceId, load]);

    const scoped = String(state.practiceId) === String(practiceId);
    return {
        lines: scoped ? state.lines : [],
        registered: scoped ? state.registered : [],
        ownedElsewhere: scoped ? state.ownedElsewhere : 0,
        sharedAccount: scoped && state.sharedAccount,
        loading: scoped && state.loading,
        loaded: scoped && state.loaded,
        error: scoped ? state.error : null,
        saving,
        reload: load,
        setUpLines,
    };
}

// "Ask an admin" for one call on a line that isn't set up (any Call Logs
// viewer). Resolves { ok, alreadyAsked, message }.
export async function askAdminAboutCall(practiceId, callId) {
    const id = String(callId || '').replace(/^voice:/, '');
    const response = await callApi(LINE_URLS.askAdmin(practiceId, id), 'POST', {});
    if (response.ok) return { ok: true, alreadyAsked: Boolean(response.data?.already_alerted_today), message: null };
    if (response.status === 0) return { ok: false, alreadyAsked: false, message: 'No reply from the server. Your request may have been sent.' };
    return { ok: false, alreadyAsked: false, message: response.data?.detail || 'Could not notify an admin.' };
}
